import { COMMON_CONSTANTS } from "src/constants/commonConstants";
import { BulkHireProgramType } from "src/constants/office.View.Constants";
import {
  BulkHireRequestAcceptPayload,
  BulkHireRequestDetail,
  BulkHireRequestItem,
  BulkHireRequestQuery,
} from "src/models/bulkConversion.models";
import { ListQueryParams, PaginatedResponse } from "src/models/common.model";
import { DeletePayloadProps } from "src/models/delete.account.models";
import { AccountEnableDisablePayload } from "src/models/enableDisableModal.model";
import { InvoiceListPayload } from "src/models/invoice.models";
import {
  BulkHireDashboard,
  BulkHireHistoryItem,
  BulkHireProfessionalItem,
  BulkHireProgramItem,
  BulkHireSetting,
  OfficeInvoicingSetup,
  OfficeInvoicingSetupPatchPayload,
  OfficeTransformedData,
} from "src/models/office.model";
import { TransformedData } from "src/models/professional.model";
import commonUtils from "src/utils/common.utils";

import API_URL from "./apiConfig/api.config";
import handleResponse from "./apiConfig/api.response.service";
import apiCall from "./apiConfig/apiCall.config";

export async function createChildOffice(
  uuid: string,
  payload: OfficeTransformedData
) {
  const url = API_URL.OFFICE.CHILD_OFFICE.replace(
    COMMON_CONSTANTS.OFFICE_UUID,
    uuid
  ).replace(/[{}]/g, "");
  return await apiCall.post(url, payload);
}
export async function createOffice(data: OfficeTransformedData) {
  return await apiCall.post(API_URL.OFFICE.OFFICE_PROFILE, data);
}
export const updateOffice = async (
  uuid: string,
  payload: OfficeTransformedData | TransformedData
) => {
  const url = API_URL.OFFICE.UPDATE_OFFICE.replace("uuid", uuid).replace(
    /[{}]/g,
    ""
  );
  return await apiCall.patch(url, payload);
};

export async function enableOrDisableOfficeAccount(
  officeUuid: string,
  data: AccountEnableDisablePayload
) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.ENABLE_DISABLE_ACCOUNT,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );

  return await apiCall.patch(url, data);
}

export async function deleteOfficeAccount(
  officeUuid: string,
  data: DeletePayloadProps,
  parentOfficeId?: string
) {
  const url = commonUtils.getUrlReplace(
    parentOfficeId && parentOfficeId !== ""
      ? API_URL.OFFICE.DELETE_CHILD_ACCOUNT
      : API_URL.OFFICE.DELETE_ACCOUNT,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );

  return await apiCall.removeWithBody(url, data);
}
export const getChildOffice = async (
  uuid?: string,
  office_type?: string,
  parent_address?: number
) => {
  const url = uuid
    ? commonUtils.getUrlReplace(
        API_URL.OFFICE.GET_CHILD_OFFICE,
        COMMON_CONSTANTS.OFFICE_UUID,
        uuid
      )
    : API_URL.OFFICE.GET_CHILD_OFFICE;

  return await apiCall.get(url, {
    parent_address: parent_address,
    office_type: office_type ?? "1",
  });
};
export async function getOfficeAccountActivity(officeUuid: string) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.GET_ACCOUNT_ACTIVITY_HISTORY,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );

  return await apiCall.get(url);
}

export const getJobHistory = async (
  uuid: string,
  payload: { [key: string]: string | number | number[] } | undefined
) => {
  const url = API_URL.DENTAL_OFFICE.GET_JOB_HISTORY.replace(
    "officeUuid",
    uuid
  ).replace(/[{}]/g, "");
  const res = await apiCall.get(url, payload);
  const responseData = await handleResponse(res);
  if (responseData.ok) {
    return responseData?.data;
  } else {
    return responseData;
  }
};
export async function downloadReport(prams: string) {
  return await apiCall.getBlobFile(API_URL.OFFICE.DOWNLOAD_REPORT + prams);
}

export const getOfficePreview = async (
  contractUuid: string,
  officeId?: string,
  pay_scale_assignment_uuid?: string
) => {
  let url = API_URL.OFFICE.CONTRACT_PREVIEW.replace(
    COMMON_CONSTANTS.CONTRACT_UUID,
    contractUuid
  )
    .replace(/[{}]/g, "")
    .replace(COMMON_CONSTANTS.OFFICE_UUID, officeId ?? "")
    .replace(/[{}]/g, "");

  if (pay_scale_assignment_uuid) {
    url += `?pay_scale_assignment_uuid=${pay_scale_assignment_uuid}`;
  }

  const res = await apiCall.getBlobFile(url);
  return res;
};

export async function getOfficeInvoices(
  officeUuid: string,
  params?: InvoiceListPayload
) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.GET_OFFICE_INVOICES,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );
  const res = await apiCall.get(url, {
    ...params,
  });
  const responseData = await handleResponse(res);

  return responseData;
}

export async function getOfficeInvoicingSetup(officeUuid: string) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.GET_INVOICING_SETUP,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );
  const res = await apiCall.get<OfficeInvoicingSetup>(url, {});
  return await handleResponse(res);
}

export async function patchOfficeInvoicingSetup(
  officeUuid: string,
  payload: OfficeInvoicingSetupPatchPayload
) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.PATCH_INVOICING_SETUP,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );
  const res = await apiCall.patch(url, payload);
  return await handleResponse(res);
}

export async function patchInvoiceCopyReference(
  officeUuid: string,
  payload: { receive_invoice_copy: boolean }
) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.PATCH_INVOICE_COPY_REFERENCE,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );
  const res = await apiCall.patch(url, payload);
  return await handleResponse(res);
}

export async function editSecondaryEmail(
  uuid: string,
  payload: { email: string }
) {
  const url = commonUtils.getUrlReplace(
    API_URL.DENTAL_OFFICE.EDIT_SECONDARY_EMAIL,
    COMMON_CONSTANTS.OFFICE_UUID,
    uuid
  );
  return await apiCall.patch(url, payload);
}

export async function editOfficeContactFields(
  officeUuid: string,
  payload: {
    can_receive_texts?: boolean;
    contact_person?: string;
    phone_number?: string;
  }
) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.PATCH_CONTACT_FIELDS,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );
  return await apiCall.patch(url, payload);
}

const withListQuery = (
  query: { [key: string]: string | number | undefined },
  params?: ListQueryParams
) => {
  if (params?.limit) query.limit = params.limit;
  if (params?.page) query.page = params.page;
  if (params?.sort) query.sort = params.sort;
  return query;
};

export async function getBulkHirePrograms(officeUuid: string) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.BULK_HIRE_PROGRAMS,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );
  const res = await apiCall.get<BulkHireProgramItem[]>(url);
  return await handleResponse(res);
}

export async function getBulkHireDashboard(
  officeUuid: string,
  programUuid: string
) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.BULK_HIRE_DASHBOARD,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );
  const res = await apiCall.get<BulkHireDashboard>(url, {
    program_uuid: programUuid,
  });
  return await handleResponse(res);
}

export async function getBulkHireHistory(
  officeUuid: string,
  params?: ListQueryParams & { status?: number }
) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.BULK_HIRE_HISTORY,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );
  const query = withListQuery({}, params);
  if (params?.status) query.status = params.status;
  const res = Object.keys(query).length
    ? await apiCall.get<PaginatedResponse<BulkHireHistoryItem>>(url, query)
    : await apiCall.get<PaginatedResponse<BulkHireHistoryItem>>(url);
  return await handleResponse(res);
}

export async function getBulkHireProfessionals(
  officeUuid: string,
  type: string,
  programUuid?: string,
  params?: ListQueryParams & {
    professional?: string;
    role_uuid?: string;
    status?: number;
  }
) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.BULK_HIRE_PROFESSIONALS,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );
  const query = withListQuery(
    {
      type,
      ...(programUuid ? { program_uuid: programUuid } : {}),
    },
    params
  );
  if (params?.professional) query.professional = params.professional;
  if (params?.role_uuid) query.role_uuid = params.role_uuid;
  if (params?.status) query.status = params.status;
  const res = await apiCall.get<PaginatedResponse<BulkHireProfessionalItem>>(
    url,
    query
  );
  return await handleResponse(res);
}

export async function changeBulkHireRequestDp(
  requestUuid: string,
  payload: BulkHireRequestAcceptPayload
) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.BULK_HIRE_REQUEST_CHANGE_DP,
    "requestUuid",
    requestUuid
  );
  const res = await apiCall.patch(url, payload);
  return await handleResponse(res);
}

export async function acceptBulkHireRequest(
  requestUuid: string,
  payload: BulkHireRequestAcceptPayload
) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.BULK_HIRE_REQUEST_ACCEPT,
    "requestUuid",
    requestUuid
  );
  const res = await apiCall.patch(url, payload);
  return await handleResponse(res);
}

export async function getBulkHireRequest(requestUuid: string) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.BULK_HIRE_REQUEST_DETAIL,
    "requestUuid",
    requestUuid
  );
  const res = await apiCall.get<BulkHireRequestDetail>(url);
  return await handleResponse(res);
}

export async function getBulkHireRequests(params?: BulkHireRequestQuery) {
  const res = await apiCall.get<PaginatedResponse<BulkHireRequestItem>>(
    API_URL.OFFICE.BULK_HIRE_REQUEST,
    params
  );
  return await handleResponse(res);
}

export async function getBulkHireSetting() {
  const res = await apiCall.get<BulkHireSetting>(
    API_URL.OFFICE.BULK_HIRE_SETTING
  );
  return await handleResponse(res);
}

export async function patchBulkHireSetting(payload: BulkHireSetting) {
  const res = await apiCall.patch(API_URL.OFFICE.BULK_HIRE_SETTING, payload);
  return await handleResponse(res);
}

export async function releaseBulkHireAddendum(
  officeUuid: string,
  payload: { program_type: BulkHireProgramType }
) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.BULK_HIRE_ADDENDUM,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );
  return await apiCall.post(url, payload);
}

export async function editOfficeMobile(
  officeUuid: string,
  payload: { mobile: string }
) {
  const url = commonUtils.getUrlReplace(
    API_URL.OFFICE.PATCH_OFFICE_MOBILE,
    COMMON_CONSTANTS.OFFICE_UUID,
    officeUuid
  );
  return await apiCall.patch(url, payload);
}

/*
  Service for setting standard working hours
  @param uuid: string
  @param payload: { default_working_hours: unknown[] }
  @returns Promise<unknown>
*/
export async function editWorkingHours(
  uuid: string,
  payload: { default_working_hours: unknown[] }
) {
  const url = commonUtils.getUrlReplace(
    API_URL.DENTAL_OFFICE.EDIT_WORKING_HOURS,
    COMMON_CONSTANTS.OFFICE_UUID,
    uuid
  );
  return await apiCall.patch(url, payload);
}
